import { ErrorBadge } from '@plato/ui-legacy/components/ui/error-badge';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import { ActivityFeedModelType } from '../../../server/types/customer-activity-feed-types.ts';
import { useActivityFeed } from '../../activity-feed/hooks/use-activity-feed.ts';
import { useActivityOpenTasks } from '../../activity-feed/hooks/use-activity-open-tasks.ts';
import { useActivityUpcomingEvents } from '../../activity-feed/hooks/use-activity-upcoming-events.ts';
import { useLoadMoreRef } from '../hooks/use-load-more-ref.ts';
import { groupUpcoming } from '../lib/group-upcoming.ts';
import { ActivityDay } from './activity-day.tsx';
import { ActivityFeedRecordItem } from './activity-feed-record-item.tsx';
import { ActivityFeedTaskItem } from './activity-feed-task-item.tsx';
import { FeedSection } from './feed-section.tsx';
import { PinnedNotesSection } from './pinned-notes-section.tsx';
import { UpcomingEventItem } from './upcoming-event-item.tsx';

const modelTypes = [
    ActivityFeedModelType.QUOTE,
    ActivityFeedModelType.ORDER,
    ActivityFeedModelType.NOTE,
    ActivityFeedModelType.TASK,
    ActivityFeedModelType.INSIGHT_OUTCOME,
    ActivityFeedModelType.CUSTOMER_STATUS_CHANGE,
];

export type CustomerActivityFeedProps = {
    customerBasics: CustomerDTO;
};

/**
 * One customer's feed: "Anstehend" (their upcoming events and open tasks) and
 * "Vergangen" (their activity, paged), each grouped by day.
 */
export function CustomerActivityFeed({ customerBasics }: CustomerActivityFeedProps): ReactElement {
    const { t } = useTranslation();
    const { customerId } = customerBasics;

    const openTasks = useActivityOpenTasks(customerId);
    const upcomingEvents = useActivityUpcomingEvents(customerId);
    const timeline = useActivityFeed({ customerId, modelTypes, keepPreviousList: true });

    const upcomingDays = useMemo(
        () => groupUpcoming(upcomingEvents.events, openTasks.taskData),
        [upcomingEvents.events, openTasks.taskData],
    );

    const loadMoreRef = useLoadMoreRef(timeline);
    const error = openTasks.error || upcomingEvents.error || timeline.error;

    return (
        <div className="@container/feed flex flex-col gap-section">
            <PinnedNotesSection customerId={customerId} />
            <FeedSection
                title={t('tasks__group_upcoming', { ns: 'tasks' })}
                isLoading={openTasks.isLoading || upcomingEvents.isLoading}
                isEmpty={upcomingDays.length === 0}
            >
                {upcomingDays.map(({ date, entries }) => (
                    <ActivityDay key={date?.toISOString() ?? 'undated'} date={date}>
                        {entries.map((entry) =>
                            entry.type === 'event' ? (
                                <UpcomingEventItem
                                    key={`event-${entry.event.event_id}`}
                                    event={entry.event}
                                />
                            ) : (
                                <ActivityFeedTaskItem
                                    key={`task-${entry.task.taskId}`}
                                    payload={entry.task}
                                    source="customer_detail"
                                />
                            ),
                        )}
                    </ActivityDay>
                ))}
            </FeedSection>
            <FeedSection
                title={t('customer_detail__past_activities', { ns: 'customer-detail' })}
                isLoading={timeline.isLoading || timeline.isFetchingNextPage}
                isEmpty={timeline.timeGroupedData.size === 0}
                footer={<div key={timeline.data?.pages.length} ref={loadMoreRef} />}
            >
                {Array.from(timeline.timeGroupedData, ([day, records]) => (
                    <ActivityDay key={day} date={new Date(day)}>
                        {records.map((activity) => (
                            <ActivityFeedRecordItem
                                key={activity.activity_id}
                                activity={activity}
                                source="customer_detail"
                            />
                        ))}
                    </ActivityDay>
                ))}
            </FeedSection>
            {!timeline.isFetchingNextPage && error?.message && <ErrorBadge>{error.message}</ErrorBadge>}
        </div>
    );
}
